import React, { useState, useEffect, useCallback } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { styled } from "@mui/material/styles";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { isValidPhoneNumber } from "libphonenumber-js";
import { auth, RecaptchaVerifier } from "@/app/firebaseClient";
import { signInWithPhoneNumber } from "firebase/auth";

const StyledPhoneInput = styled(PhoneInput)(({ theme }) => ({
  width: "100%",
  "&& .form-control": {
    height: "42px !important",
    borderRadius: "50px !important",
    fontSize: "18px !important",
    border: `1px solid ${theme.palette.divider} !important`,
    paddingLeft: "60px !important",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    caretColor: theme.palette.text.primary,
    colorScheme: theme.palette.mode,
    "&::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
    "&:hover": {
      borderColor: `${theme.palette.text.secondary} !important`,
    },
    "&:focus": {
      borderColor: `${theme.palette.primary.main} !important`,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
  },
  "&& .flag-dropdown": {
    border: "none !important",
  },
  "&& .selected-flag .arrow": {
    borderTopColor: theme.palette.text.secondary,
    "&.up": {
      borderBottomColor: theme.palette.text.secondary,
    },
  },
  "&& .selected-flag:focus .arrow": {
    borderTopColor: theme.palette.primary.main,
    "&.up": {
      borderBottomColor: theme.palette.primary.main,
    },
  },
  "&& .country-list": {
    maxHeight: "200px",
    overflowY: "auto",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    "& .country .dial-code": {
      color: theme.palette.text.secondary,
    },
    "& .country:hover, & .country.highlight": {
      backgroundColor: theme.palette.action.selected,
    },
  },
}));

const PhoneNumberForm = ({
  stacked = false,
  countryCode,
  onPhoneSubmitted,
  setErrorMessage,
  isLoading,
  setIsLoading,
}) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (!recaptchaVerifier) {
      const verifier = new RecaptchaVerifier(
        auth,
        "phone-recaptcha-container",
        {
          size: "invisible",
          callback: () => console.log("reCAPTCHA resolved."),
        }
      );
      verifier.render().then((widgetId) => {
        setRecaptchaWidgetId(widgetId);
        setRecaptchaVerifier(verifier);
      });
    }
    return () => {
      if (recaptchaVerifier) {
        recaptchaVerifier.clear();
        setRecaptchaVerifier(null);
      }
    };
  }, [recaptchaVerifier]);

  const handleSendCode = useCallback(
    async (event) => {
      event.preventDefault();
      if (hasSubmitted) return;
      setHasSubmitted(true);
      setIsLoading(true);
      setErrorMessage("");
      if (!isValidPhoneNumber("+" + phoneNumber)) {
        setErrorMessage("Please enter a valid phone number.");
        setIsLoading(false);
        setHasSubmitted(false);
        return;
      }
      if (!recaptchaVerifier) {
        setErrorMessage("reCAPTCHA is not ready yet. Please try again.");
        setIsLoading(false);
        setHasSubmitted(false);
        return;
      }
      try {
        if (recaptchaWidgetId !== null) {
          window.grecaptcha.reset(recaptchaWidgetId);
        }
        const confirmationResult = await signInWithPhoneNumber(
          auth,
          "+" + phoneNumber,
          recaptchaVerifier
        );
        window.confirmationResult = confirmationResult;
        onPhoneSubmitted("+" + phoneNumber);
      } catch (error) {
        setErrorMessage("Failed to send code. Please try again.");
        setHasSubmitted(false); // Reset on error
      } finally {
        setIsLoading(false);
      }
    },
    [
      phoneNumber,
      recaptchaVerifier,
      recaptchaWidgetId,
      onPhoneSubmitted,
      setIsLoading,
      setErrorMessage,
      hasSubmitted,
    ]
  );

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSendCode(e);
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleSendCode]);

  return (
    <form onSubmit={handleSendCode}>
      <div id="phone-recaptcha-container"></div>
      <Box
        sx={{
          display: "flex",
          flexDirection: stacked ? "column" : { xs: "column", sm: "row" },
          alignItems: stacked ? "stretch" : "center",
          gap: stacked ? 2 : { xs: 2, sm: 0 },
        }}
      >
        <StyledPhoneInput
          country={countryCode?.toLowerCase()}
          value={phoneNumber}
          onChange={(value) => setPhoneNumber(value)}
          inputProps={{
            name: "phone",
            required: true,
            autoFocus: true,
            placeholder: "Enter your mobile number",
          }}
          specialLabel=""
          containerStyle={{ width: "100%" }}
          inputStyle={{
            width: "100%",
            height: "50px",
            borderRadius: "40px",
            fontSize: "18px",
          }}
          dropdownStyle={{ maxHeight: "90px" }}
          disableDropdownScroll={false}
          excludeCountries={["cu", "ir", "kp", "so", "sy", "ye", "af"]}
          preferredCountries={["us", "ca"]}
        />
        <Button
          type="submit"
          variant={stacked ? "text" : "contained"}
          size="medium"
          color="primary"
          disabled={isLoading || !phoneNumber || hasSubmitted}
          sx={{
            ml: stacked ? 0 : { xs: 0, sm: 3 },
            alignSelf: stacked ? "flex-end" : "auto",
            borderRadius: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: {
              xs: "48px",
              md: stacked ? "48px" : "auto",
            },
            width: { xs: "100%", sm: stacked ? "200px" : "280px" },
            ...(stacked && {
              width: "auto",
              minWidth: 0,
              px: 0.5,
              gap: 1,
              fontWeight: 600,
              color: "text.primary",
              backgroundColor: "transparent",
              "&:hover": { backgroundColor: "transparent", color: "text.secondary" },
              "&.Mui-disabled": { backgroundColor: "transparent" },
            }),
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : stacked ? null : (
            <PhoneIphoneIcon fontSize="small" />
          )}
          <Box component="span" sx={{ flexGrow: stacked ? 0 : 1, textAlign: "center" }}>
            {isLoading ? "Sending..." : "Send Code"}
          </Box>
        </Button>
      </Box>
    </form>
  );
};

export default PhoneNumberForm;
